import { useState } from 'react'
import { useQueryState } from 'nuqs'
import { RefetchOptions } from '@tanstack/react-query'

import { DataTableSearch } from '@/common/ui/table/data-table-search'
import { DataTableStatusSelector } from '@/common/ui/table/data-table-status-selector'
import { DataTableResetFilter } from '@/common/ui/table/data-table-reset-filter'
import { DataTableArrow } from '@/common/ui/table/data-table-arrow'
import { Button } from '@/common/ui/button'
import { Icons } from '@/common/ui/icons'
import { Input } from '@/common/ui/input'

import { columns } from './constants'

import type { OperatorDepositTransaction } from '@/modules/bank/type'

interface OperatorDepositTableProps {
	data: OperatorDepositTransaction[]
	total: number
	onRefresh: (options?: RefetchOptions) => Promise<unknown>
	isLoading?: boolean
	onSort?: (columnId: string, order: 'asc' | 'desc' | undefined) => void
}

const statusOptions = [
	{
		label: 'สร้างแล้ว',
		value: 'CREATED',
	},
	{
		label: 'รอดำเนินการ',
		value: 'PENDING',
	},
	{
		label: 'กำลังตรวจสอบ',
		value: 'PROCESSING',
	},
	{
		label: 'สำเร็จ',
		value: 'COMPLETED',
	},
	{
		label: 'รอตรวจสอบ',
		value: 'MANUAL_COMPLETED',
	},
	{
		label: 'ล้มเหลว',
		value: 'PAYMENT_GATEWAY_FAILED',
	},
	{
		label: 'ไม่สำเร็จ',
		value: 'FAILED',
	},
	{
		label: 'ยกเลิก',
		value: 'CANCELED',
	},
	{
		label: 'หมดเวลา',
		value: 'EXPIRED',
	},
]

const defaultStatusValues = ['CREATED', 'PENDING', 'PROCESSING']

export default function OperatorDepositTable({
	data,
	total,
	onRefresh,
	isLoading,
	onSort,
}: OperatorDepositTableProps) {
	const [localTxId, setLocalTxId] = useState('')
	const [txId, setTxId] = useQueryState('txId')

	return (
		<div className="space-y-4">
			{/* Filter Controls - Mobile Responsive */}
			<div className="space-y-4">
				{/* Search Controls Row */}
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
					{/* Transaction ID Search */}
					<div className="flex flex-col gap-2 sm:flex-row sm:gap-2">
						<Input
							placeholder="ค้นหารหัสธุรกรรม"
							value={localTxId ?? ''}
							onChange={(e) => setLocalTxId(e.target.value)}
							className="h-8 w-full text-sm sm:h-9 sm:text-base"
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									setTxId(localTxId)
								}
							}}
						/>
						<div className="flex gap-2 sm:flex-shrink-0">
							<Button
								onClick={() => setTxId(localTxId)}
								size="sm"
								className="h-8 flex-1 px-3 text-xs sm:h-9 sm:flex-initial sm:px-4 sm:text-sm"
							>
								ค้นหา
							</Button>
							{txId && (
								<Button
									variant="outline"
									size="sm"
									className="h-8 flex-1 px-3 text-xs sm:h-9 sm:flex-initial sm:px-4 sm:text-sm"
									onClick={() => {
										setTxId(null)
										setLocalTxId('')
									}}
								>
									ล้าง
								</Button>
							)}
						</div>
					</div>

					{/* Payment Gateway ID Search */}
					<div className="w-full">
						<DataTableSearch placeHolder="ค้นหาจาก ID" />
					</div>
				</div>

				{/* Filter Controls Row */}
				<div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
					<div className="w-full sm:w-auto">
						<DataTableResetFilter />
					</div>

					<Button
						variant="outline"
						size="sm"
						onClick={() => onRefresh()}
						title="รีเฟรชข้อมูล"
						className="h-8 w-8 sm:h-9 sm:w-9"
					>
						<Icons.refresh className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Status Filter - Full Width */}
			<div className="w-full">
				<DataTableStatusSelector
					options={statusOptions}
					label="สถานะ"
					paramKey="status"
					defaultValues={defaultStatusValues}
				/>
			</div>

			{/* Data Table */}
			<DataTableArrow
				columns={columns}
				data={data}
				total={total}
				isLoading={isLoading}
				onSort={onSort}
			/>
		</div>
	)
}
